/**
 * AddProductDialog Component
 * Dialog for adding new product instances to a company
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Info } from 'lucide-react';
import type { ProductInstance, ProductType } from '@/features/multi-company/types/company.types';
import {
  PRODUCT_CATALOG,
  getAvailableProducts,
  generateMockInstanceName,
  shouldAutoConnect,
  createAutoConnectedInstance,
} from '@/features/multi-company/types/product.types';

interface AddProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyCode: string;
  existingInstances: ProductInstance[];
  onAdd: (instances: ProductInstance[]) => void;
}

export function AddProductDialog({
  open,
  onOpenChange,
  companyId,
  companyCode,
  existingInstances,
  onAdd,
}: AddProductDialogProps) {
  const [selectedProduct, setSelectedProduct] = useState<ProductType | ''>('');
  const [instanceName, setInstanceName] = useState('');
  const [error, setError] = useState('');

  const availableProducts = getAvailableProducts(existingInstances);

  const generateInstanceNameOptions = (productType: ProductType): string[] => {
    const options: string[] = [];
    for (let i = 0; i < 5; i++) {
      options.push(generateMockInstanceName(productType, companyCode));
    }
    return options;
  };

  const [instanceNameOptions, setInstanceNameOptions] = useState<string[]>([]);

  const handleProductChange = (value: string) => {
    const productType = value as ProductType;
    setSelectedProduct(productType);
    const options = generateInstanceNameOptions(productType);
    setInstanceNameOptions(options);
    setInstanceName(options[0]); // Set first option as default
    setError('');
  };

  const handleAdd = () => {
    if (!selectedProduct) {
      setError('Please select a product');
      return;
    }

    if (!instanceName.trim()) {
      setError('Please enter an instance name');
      return;
    }

    // Check if instance name already exists
    if (existingInstances.some(i => i.instanceName === instanceName.trim())) {
      setError('An instance with this name already exists');
      return;
    }

    const newInstances: ProductInstance[] = [];

    // Create the main product instance
    const mainInstance: ProductInstance = {
      id: `prod-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      companyId,
      productType: selectedProduct,
      instanceName: instanceName.trim(),
      connectionStatus: 'not_configured',
      apiEndpoint: null,
      apiKey: null,
      lastSync: null,
      lastTestDate: null,
      lastTestStatus: null,
      syncFrequency: 'daily',
      autoSync: false,
      config: {},
      errorMessage: null,
    };

    newInstances.push(mainInstance);

    // Handle auto-connections for Access Financials Operations
    const autoConnectProducts = shouldAutoConnect(selectedProduct);
    if (autoConnectProducts.length > 0) {
      autoConnectProducts.forEach(productType => {
        // Only auto-connect if the product doesn't already exist
        if (!existingInstances.some(i => i.productType === productType)) {
          const autoInstance = createAutoConnectedInstance(
            mainInstance.id,
            productType,
            companyId,
            companyCode
          );
          newInstances.push(autoInstance);
        }
      });
    }

    onAdd(newInstances);
    handleClose();
  };

  const handleClose = () => {
    setSelectedProduct('');
    setInstanceName('');
    setError('');
    onOpenChange(false);
  };

  const selectedProductInfo = selectedProduct ? PRODUCT_CATALOG[selectedProduct] : null;
  const willAutoConnect = selectedProduct ? shouldAutoConnect(selectedProduct) : [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Product</DialogTitle>
          <DialogDescription>
            Select a product and provide an instance name for integration
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="product">Product</Label>
            <Select value={selectedProduct} onValueChange={handleProductChange}>
              <SelectTrigger id="product">
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent>
                {availableProducts.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    All products have been added
                  </div>
                ) : (
                  availableProducts.map((productType) => {
                    const info = PRODUCT_CATALOG[productType];
                    return (
                      <SelectItem key={productType} value={productType}>
                        <div className="flex flex-col">
                          <span className="font-medium">{info.displayName}</span>
                          <span className="text-xs text-muted-foreground">
                            {info.description}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedProductInfo && (
            <div className="space-y-2">
              <Label htmlFor="instanceName">Instance Name</Label>
              <Select value={instanceName} onValueChange={setInstanceName}>
                <SelectTrigger id="instanceName">
                  <SelectValue placeholder="Select an instance name" />
                </SelectTrigger>
                <SelectContent>
                  {instanceNameOptions.map((name, index) => (
                    <SelectItem key={index} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                A unique identifier for this product instance
              </p>
            </div>
          )}

          {willAutoConnect.length > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Auto-connection:</strong> Adding {selectedProductInfo?.displayName} will
                automatically connect the following products:
                <ul className="mt-2 ml-4 list-disc text-sm">
                  {willAutoConnect.map(productType => (
                    <li key={productType}>{PRODUCT_CATALOG[productType].displayName}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!selectedProduct || availableProducts.length === 0}>
            Add Product
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
